import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  StyleSheet,
  Platform,
} from 'react-native';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';

const STEPPER_BTN_SIZE = 32;

export default function HeightPicklistDropdown({
  label,
  value,
  options,
  onSelect,
  onAddOption,
  theme,
  disabled = false,
  inlineLabelWidth,
}) {
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const { styles, arrowColor, addColor } = getStyles(theme);

  const display = value ? String(value) : 'Select height…';

  const handleAdd = async () => {
    const trimmed = newLabel.trim();
    if (!trimmed || !onAddOption) return;
    setSaving(true);
    try {
      const created = await onAddOption(trimmed);
      if (created?.height_label) {
        onSelect(created.height_label);
        setNewLabel('');
        setAdding(false);
        setOpen(false);
      }
    } finally {
      setSaving(false);
    }
  };

  const menuBlock = open ? (
    <View style={styles.menu}>
      {(options || []).map((opt) => {
        const selected = value === opt.height_label;
        return (
          <TouchableOpacity
            key={`${opt.id}-${opt.height_label}`}
            style={[styles.menuItem, selected && styles.menuItemSelected]}
            onPress={() => {
              onSelect(opt.height_label);
              setOpen(false);
            }}
          >
            <Text style={styles.menuItemText}>{opt.height_label}</Text>
            {opt.student_id != null ? (
              <Text style={styles.menuItemMine}>Mine</Text>
            ) : null}
          </TouchableOpacity>
        );
      })}
      {adding ? (
        <View style={styles.addRow}>
          <TextInput
            style={styles.addInput}
            value={newLabel}
            onChangeText={setNewLabel}
            placeholder="New height label"
            placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
            editable={!saving}
          />
          <TouchableOpacity
            style={styles.addSaveBtn}
            onPress={() => void handleAdd()}
            disabled={saving || !newLabel.trim()}
          >
            <Text style={styles.addSaveText}>Save</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <TouchableOpacity
          style={styles.addNewBtn}
          onPress={() => setAdding(true)}
        >
          <MaterialIcons name="add" size={18} color={addColor} />
          <Text style={styles.addNewText}>Add new height</Text>
        </TouchableOpacity>
      )}
    </View>
  ) : null;

  const toggleButton = (
    <TouchableOpacity
      style={[
        styles.toggle,
        inlineLabelWidth ? styles.toggleInline : null,
        disabled && styles.toggleDisabled,
      ]}
      onPress={() => !disabled && setOpen((o) => !o)}
      disabled={disabled}
      activeOpacity={0.7}
    >
      <Text style={[styles.toggleText, !value && styles.togglePlaceholder]} numberOfLines={1}>
        {display}
      </Text>
      <MaterialIcons
        name={open ? 'keyboard-arrow-up' : 'keyboard-arrow-down'}
        size={22}
        color={arrowColor}
      />
    </TouchableOpacity>
  );

  if (inlineLabelWidth) {
    return (
      <View style={styles.wrapInline}>
        <View style={[styles.inlineLabelCol, { width: inlineLabelWidth }]}>
          {label ? <Text style={styles.labelInline}>{label}</Text> : null}
        </View>
        <View style={styles.inlineControlCol}>
          {toggleButton}
          {menuBlock}
        </View>
      </View>
    );
  }

  return (
    <View style={styles.wrap}>
      {label ? <Text style={styles.label}>{label}</Text> : null}
      {toggleButton}
      {menuBlock}
    </View>
  );
}

const getStyles = (theme) => {
  const arrowColor = theme.isDarkMode ? '#FFFFFF' : '#333333';
  const addColor = theme.colors.primary || '#d4af37';

  const styles = StyleSheet.create({
    wrap: {
      minWidth: 120,
      maxWidth: 200,
      backgroundColor: 'transparent',
    },
    wrapInline: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      width: '100%',
      minWidth: 0,
      backgroundColor: 'transparent',
    },
    inlineLabelCol: {
      flexShrink: 0,
      justifyContent: 'center',
      minHeight: STEPPER_BTN_SIZE,
      backgroundColor: 'transparent',
    },
    inlineControlCol: {
      flex: 1,
      minWidth: 0,
      backgroundColor: 'transparent',
      ...(Platform.OS === 'web' ? { maxWidth: '100%', overflow: 'hidden' } : {}),
    },
    label: {
      fontSize: 12,
      color: theme.colors.text,
      marginBottom: 4,
      backgroundColor: 'transparent',
    },
    labelInline: {
      fontSize: 12,
      fontWeight: '600',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    toggle: {
      flexDirection: 'row',
      alignItems: 'center',
      borderWidth: 1,
      borderColor: theme.isDarkMode ? theme.colors.border : '#CCCCCC',
      borderRadius: 6,
      paddingVertical: 6,
      paddingHorizontal: 8,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    toggleInline: {
      width: '100%',
      minHeight: STEPPER_BTN_SIZE,
      paddingVertical: 4,
    },
    toggleDisabled: {
      opacity: 0.5,
    },
    toggleText: {
      flex: 1,
      fontSize: 13,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    togglePlaceholder: {
      opacity: 0.55,
    },
    menu: {
      marginTop: 4,
      borderWidth: 1,
      borderColor: theme.isDarkMode ? theme.colors.border : '#E0E0E0',
      borderRadius: 6,
      overflow: 'hidden',
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
      ...(Platform.OS === 'web' ? { zIndex: 20 } : {}),
    },
    menuItem: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 10,
      paddingHorizontal: 10,
      borderBottomWidth: 1,
      borderBottomColor: theme.isDarkMode ? theme.colors.border : '#F0F0F0',
    },
    menuItemSelected: {
      backgroundColor: theme.isDarkMode ? 'rgba(212, 175, 55, 0.2)' : 'rgba(212, 175, 55, 0.15)',
    },
    menuItemText: {
      flex: 1,
      fontSize: 14,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    menuItemMine: {
      fontSize: 10,
      fontWeight: '600',
      color: addColor,
      backgroundColor: 'transparent',
    },
    addNewBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: 10,
      backgroundColor: 'transparent',
    },
    addNewText: {
      marginLeft: 4,
      fontSize: 13,
      fontWeight: '600',
      color: addColor,
      backgroundColor: 'transparent',
    },
    addRow: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: 8,
      backgroundColor: 'transparent',
    },
    addInput: {
      flex: 1,
      borderWidth: 1,
      borderColor: theme.isDarkMode ? theme.colors.border : '#CCCCCC',
      borderRadius: 6,
      paddingVertical: 6,
      paddingHorizontal: 8,
      fontSize: 13,
      color: theme.colors.text,
      marginRight: 6,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    addSaveBtn: {
      paddingVertical: 6,
      paddingHorizontal: 10,
      borderRadius: 6,
      backgroundColor: addColor,
    },
    addSaveText: {
      color: '#FFFFFF',
      fontSize: 12,
      fontWeight: '600',
      backgroundColor: 'transparent',
    },
  });
  return { styles, arrowColor, addColor };
};
